/**
 * Replay engine pour la queue offline-first — Phase 3.3.1.
 *
 * Coordonne :
 *
 *   - **Détection offline** : si `navigator.onLine === false` au
 *     moment où un caller veut envoyer une mutation, on enqueue
 *     directement (pas de round-trip réseau inutile).
 *   - **Capture des échecs réseau** : si `fetch` throw `TypeError`
 *     (= réseau coupé pendant la requête), on enqueue après coup.
 *     Distinct d'`ApiError` (= serveur a répondu mais avec une
 *     erreur métier — on ne replay PAS, le serveur sait ce qu'il
 *     fait).
 *   - **Replay au démarrage et au `online` event** : drain de la
 *     queue dans l'ordre FIFO, best-effort. Une mutation qui
 *     échoue à nouveau (4xx serveur) est dropped après log ; une
 *     qui ré-échoue réseau reste en queue.
 *
 * **Garde-fous** :
 *
 *   - Cap `MAX_ATTEMPTS` par mutation : après 5 échecs, on drop +
 *     log. Évite une queue qui grossit pour toujours sur une
 *     mutation cassée.
 *   - **Pas de retry serré** : une seule passe de replay par
 *     trigger (boot ou online). Les retries futurs nécessiteront
 *     un nouvel `online` event ou un reload.
 *   - **Idempotence du replay** : `replayPendingMutations()` est
 *     guard par `inFlight` — un double appel concurrent = un seul
 *     drain.
 *
 * **Hors scope 3.3.1** :
 *
 *   - Pas d'idempotency-key côté HTTP (différé à 3.3.2+).
 *   - Pas de backoff exponentiel — on retente brut au prochain
 *     online event.
 *   - Pas de notification UI « X mutations en attente » — différé
 *     à 3.3.2+ avec un store dédié.
 *   - Pas d'intégration dans les api/*.ts existants — différé à
 *     3.3.2+. En 3.3.1, on expose juste les helpers ; le câblage
 *     dans events/tasks/messages/etc. se fera incrément par
 *     incrément.
 */
import { ApiError, apiRequest } from './client.js';
import {
  enqueueMutation,
  incrementMutationAttempts,
  listPendingMutations,
  removeMutation,
} from '../storage/mutationsQueue.js';
import { deleteBlob, getBlob } from '../storage/blobs.js';
import { bumpPendingCount, hydratePendingCount } from '../stores/mutationsQueueStore.js';
import { showToast } from '../ui/Toast.js';
import { t } from '../i18n/index.js';
import type { MutationEntry, MutationMedia } from '../storage/db.js';
import { emitLocalIdRemap } from './localIdRemap.js';

/**
 * Plafond d'essais par mutation avant drop. 5 = empirique. Si on
 * voit des mutations dropped récurrentes en prod (logs Sentry),
 * on augmentera. Si on voit la queue gonfler sans drainer, on
 * baissera pour révéler les bugs.
 */
export const MAX_ATTEMPTS = 5;

/**
 * Une mutation queue-able. Subset d'`ApiRequestOptions` —
 * méthodes mutantes uniquement (GET serait dénué de sens).
 */
export interface QueueableMutation {
  method: 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  /**
   * Phase 3.3.7 — id local pré-assigné par le store domain pour
   * un POST de création. Au success serveur, on extrait l'id
   * UUID retourné et on émet un event `nestor:mutation-remap
   * {localId, serverId, path}` que le broker écoute pour
   * remapper le store local. Si absent, pas d'émission.
   */
  localId?: string;
}

/** Détecte si on est offline d'après l'API navigateur. */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Détecte si une erreur de fetch est causée par un problème réseau
 * (vs une erreur métier du serveur). `TypeError` = `fetch failed`,
 * `AbortError` aussi (timeout). `ApiError` = serveur a répondu, ne
 * doit PAS être enqueued (le serveur sait ce qu'il fait — un 400
 * ne va pas devenir un 201 au prochain essai).
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof ApiError) return false;
  if (err instanceof TypeError) return true;
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  // Fallback : si on a un Error sans status (ce que ApiError porte
  // toujours), c'est probablement une erreur réseau.
  return err instanceof Error && !('status' in err);
}

/**
 * Tente la mutation HTTP ; si elle échoue par perte réseau (ou si
 * on est offline d'entrée), enqueue pour replay plus tard.
 *
 * Sémantique de retour :
 *
 *   - Si la requête a abouti : retourne la réponse parsée (typed `T`).
 *   - Si on était offline ou si fetch a TypeError → enqueue + retourne
 *     `{queued: true, seq}` (le caller peut décider d'afficher un
 *     feedback optimiste à l'utilisateur).
 *   - Si le serveur a renvoyé une `ApiError` (4xx, 5xx) → rethrow
 *     comme d'habitude (pas d'enqueue : le serveur sait).
 *
 * Le caller décide ce qu'il fait du `{queued: true}` — typiquement
 * un toast « Sera synchronisé dès la reconnexion » + une mise à
 * jour optimiste du store local.
 */
export type SendResult<T> = { kind: 'sent'; data: T } | { kind: 'queued'; seq: number | null };

export async function sendOrQueueMutation<T>(mutation: QueueableMutation): Promise<SendResult<T>> {
  // Court-circuit offline : pas la peine de tenter le fetch.
  if (isOffline()) {
    const seq = await enqueueMutation({
      method: mutation.method,
      path: mutation.path,
      body: mutation.body ?? null,
      ...(mutation.localId ? { localId: mutation.localId } : {}),
    });
    notifyQueued();
    return { kind: 'queued', seq };
  }

  try {
    const data = await apiRequest<T>(mutation.path, {
      method: mutation.method,
      ...(mutation.body !== undefined ? { body: mutation.body } : {}),
    });
    // Phase 3.3.7 : succès online direct → si on a un localId,
    // tente d'extraire l'id serveur du body et émet l'event remap.
    maybeEmitRemap(mutation.path, mutation.localId, data);
    return { kind: 'sent', data };
  } catch (err) {
    if (isNetworkError(err)) {
      const seq = await enqueueMutation({
        method: mutation.method,
        path: mutation.path,
        body: mutation.body ?? null,
        ...(mutation.localId ? { localId: mutation.localId } : {}),
      });
      notifyQueued();
      return { kind: 'queued', seq };
    }
    // Erreur serveur (ApiError) → rethrow, le caller doit la
    // traiter (afficher un message d'erreur, garder le state local
    // intact, etc.).
    throw err;
  }
}

/**
 * Extrait l'id serveur d'une réponse API typée et émet l'event
 * `nestor:mutation-remap` si un `localId` est fourni.
 *
 * Les réponses POST de Nestor suivent toutes le pattern
 * `{<wrapperField>: {id: 'uuid-...', ...}}` — `event`, `task`,
 * `message`, `photo`, `entry`, `expense`, `item`. On ne sait pas
 * lequel — on récupère la première valeur dont la shape contient
 * un `id` string. Sécurisé : si le shape inattendu, no-op (le
 * remap est best-effort, la sync au prochain GET réparera).
 */
function maybeEmitRemap(path: string, localId: string | undefined, data: unknown): void {
  if (!localId) return;
  if (typeof data !== 'object' || data === null) return;
  for (const value of Object.values(data)) {
    if (typeof value === 'object' && value !== null && 'id' in value) {
      const serverId = (value as { id: unknown }).id;
      if (typeof serverId === 'string') {
        emitLocalIdRemap({ path, localId, serverId });
        return;
      }
    }
  }
}

/**
 * Effets de bord UI après un enqueue — Phase 3.3.4.
 *
 * 1. `bumpPendingCount()` : bump optimiste du store réactif pour que
 *    le badge UI réagisse immédiatement.
 * 2. `showToast(...)` : feedback visuel ton bienveillant. Le toast
 *    no-op si `mountToastHost` n'a pas été appelé (cas des tests
 *    unit qui n'instancient pas le host).
 *
 * Wrappé dans un try/catch défensif : même si une dépendance UI
 * panique (i18n non chargé, DOM absent), la mutation reste persistée
 * et le flot offline continue de fonctionner.
 */
function notifyQueued(): void {
  try {
    bumpPendingCount();
    showToast({ message: t('mutations_queued'), kind: 'info' });
  } catch (err) {
    console.warn('[mutations-replay] notifyQueued side-effect failed:', err);
  }
}

let replayInFlight: Promise<void> | null = null;

/**
 * Drain de la queue : itère les mutations en attente dans l'ordre
 * FIFO et tente chacune. Idempotent : un appel concurrent est
 * dédupliqué via `replayInFlight`.
 *
 * Sémantique par mutation :
 *
 *   - Succès → `removeMutation(seq)` puis on continue.
 *   - Échec réseau (TypeError) → incrémente attempts. Si
 *     `attempts >= MAX_ATTEMPTS`, drop + warn. Sinon laisse en
 *     queue pour le prochain online event.
 *   - Échec serveur (ApiError) → drop + warn. Le serveur ne
 *     changera pas d'avis ; insister est nocif (ex. 403 si la
 *     session a expiré, retenter ne fera pas réapparaître les
 *     droits).
 *
 * **Stop on first network error** : si une mutation échoue
 * réseau, on arrête tout de suite le drain — inutile de tenter
 * les suivantes qui échoueront pareil. Le prochain online event
 * relancera.
 */
export async function replayPendingMutations(): Promise<void> {
  if (replayInFlight) return replayInFlight;
  replayInFlight = doReplay().finally(() => {
    replayInFlight = null;
  });
  return replayInFlight;
}

/**
 * Outcome interne d'un replay step. `success` = mutation drainée
 * proprement (à comptabiliser dans `drainedSuccess`). `dropped` =
 * mutation retirée mais sans compter comme succès (server error /
 * blob disparu / max attempts). `keep` = laisse en queue, break.
 */
type ReplayStepOutcome = 'success' | 'dropped' | 'keep';

/**
 * Replay JSON simple — comportement initial 3.3.1. Un seul
 * `apiRequest` ; succès → remove ; network err → incrementAttempts
 * et break ; server err → drop.
 */
async function replayJsonMutation(m: MutationEntry): Promise<ReplayStepOutcome> {
  if (m.seq === undefined) return 'dropped';
  try {
    const data = await apiRequest(m.path, {
      method: m.method,
      ...(m.body !== null && m.body !== undefined ? { body: m.body } : {}),
    });
    // Phase 3.3.7 : si la mutation portait un `localId`, émet le
    // remap event après extraction de l'id serveur de la réponse.
    maybeEmitRemap(m.path, m.localId, data);
    await removeMutation(m.seq);
    return 'success';
  } catch (err) {
    if (isNetworkError(err)) {
      const attempts = await incrementMutationAttempts(m.seq);
      if (attempts !== null && attempts >= MAX_ATTEMPTS) {
        console.warn('[mutations-replay] drop after max attempts', {
          seq: m.seq,
          method: m.method,
          path: m.path,
        });
        await removeMutation(m.seq);
        return 'dropped';
      }
      return 'keep';
    }
    console.warn('[mutations-replay] drop on server error', {
      seq: m.seq,
      method: m.method,
      path: m.path,
      error: err instanceof ApiError ? (err.code ?? err.status) : err,
    });
    await removeMutation(m.seq);
    return 'dropped';
  }
}

/**
 * Replay binaire — Phase 3.3.5. Flow 3-step :
 *
 *   1. `getBlob(media.blobKey)` côté IDB. Si null (quota / purge),
 *      mutation dropped (rien à uploader, log warning).
 *   2. POST `media.uploadUrlPath` → presigned R2 + id pré-réservé.
 *   3. POST FormData R2 (URL externe) avec le blob.
 *   4. POST `media.confirmPath` avec body adapté (`voicemail` ou
 *      `photo`) + extras + ids du presign.
 *   5. Succès → `removeMutation` + `deleteBlob` (R2 en a la copie).
 *
 * Erreurs :
 *
 *   - Blob disparu → drop (warn).
 *   - Network err sur n'importe quelle étape → keep + bump attempts.
 *   - Server err sur presign / confirm → drop + warn.
 *   - R2 PUT échec (non-2xx) → traité comme network err (le bucket
 *     peut être down temporairement).
 */
async function replayMediaMutation(m: MutationEntry): Promise<ReplayStepOutcome> {
  if (m.seq === undefined || !m.media) return 'dropped';
  const media: MutationMedia = m.media;

  // 1. Récupère le blob source.
  const blob = await getBlob(media.blobKey);
  if (!blob) {
    console.warn('[mutations-replay] media blob missing, dropping mutation', {
      seq: m.seq,
      blobKey: media.blobKey,
    });
    await removeMutation(m.seq);
    return 'dropped';
  }

  try {
    // 2. Obtient un presigned R2 frais.
    const upload = await apiRequest<UploadUrlResponse>(media.uploadUrlPath, {
      method: 'POST',
      body: { mime: media.mime, byteSize: media.byteSize },
    });

    // 3. PUT R2 via FormData (URL externe, hors apiRequest).
    const form = new FormData();
    for (const [k, v] of Object.entries(upload.uploadFields)) {
      form.append(k, v);
    }
    form.append('file', blob);
    const r2Res = await fetch(upload.uploadUrl, { method: 'POST', body: form });
    if (!r2Res.ok) {
      // R2 a refusé (bucket plein, presigned expiré, etc.). Traite
      // comme un network err — retry au prochain online.
      throw new TypeError(`r2 upload failed: ${r2Res.status.toString()}`);
    }

    // 4. Confirm — body shape selon kind.
    const confirmBody = buildMediaConfirmBody(media, upload);
    const data = await apiRequest(media.confirmPath, {
      method: 'POST',
      body: confirmBody,
    });
    // Phase 3.3.7 : remap localId → serverId si défini.
    maybeEmitRemap(media.confirmPath, m.localId, data);

    // 5. Succès — purge la mutation + le blob (R2 a la copie).
    await removeMutation(m.seq);
    await deleteBlob(media.blobKey);
    return 'success';
  } catch (err) {
    if (isNetworkError(err)) {
      const attempts = await incrementMutationAttempts(m.seq);
      if (attempts !== null && attempts >= MAX_ATTEMPTS) {
        console.warn('[mutations-replay] drop media after max attempts', {
          seq: m.seq,
          blobKey: media.blobKey,
        });
        await removeMutation(m.seq);
        // Garde le blob IDB — l'utilisateur peut peut-être retenter
        // manuellement. Le cleanup viendra avec une UI "réessayer"
        // ou expirera au prochain logout.
        return 'dropped';
      }
      return 'keep';
    }
    console.warn('[mutations-replay] drop media on server error', {
      seq: m.seq,
      blobKey: media.blobKey,
      error: err instanceof ApiError ? (err.code ?? err.status) : err,
    });
    await removeMutation(m.seq);
    return 'dropped';
  }
}

/**
 * Shape de la réponse de `POST .../upload-url` côté serveur — commun
 * aux photos et voicemails. On accepte les champs des deux flavors
 * (`messageId` pour voicemail, `photoId` pour photo) — l'un sera
 * défini, l'autre `undefined`.
 */
interface UploadUrlResponse {
  uploadUrl: string;
  uploadFields: Record<string, string>;
  mediaKey: string;
  expiresIn: number;
  messageId?: string;
  photoId?: string;
}

/**
 * Construit le body du confirm POST selon la flavor (voicemail nested
 * vs photo flat). Cohérent avec les schémas zod côté serveur
 * (`apps/api/src/routes/messages.ts:audioConfirmSchema` et
 * `apps/api/src/routes/photos.ts:confirmSchema`).
 */
function buildMediaConfirmBody(
  media: MutationMedia,
  upload: UploadUrlResponse,
): Record<string, unknown> {
  if (media.confirmKind === 'voicemail') {
    if (!upload.messageId) {
      throw new ApiError(500, 'upload_url_missing_messageId', null);
    }
    return {
      ...media.confirmExtras,
      audio: {
        messageId: upload.messageId,
        mediaKey: upload.mediaKey,
        mime: media.mime,
        byteSize: media.byteSize,
      },
    };
  }
  // photo
  if (!upload.photoId) {
    throw new ApiError(500, 'upload_url_missing_photoId', null);
  }
  return {
    ...media.confirmExtras,
    photoId: upload.photoId,
    mediaKey: upload.mediaKey,
    mime: media.mime,
    byteSize: media.byteSize,
  };
}

async function doReplay(): Promise<void> {
  if (isOffline()) return; // Inutile de tenter.

  const pending = await listPendingMutations();
  // Phase 3.3.4 : track le nombre de mutations effectivement drainées
  // (réussies côté serveur) pour décider d'afficher le toast `synced`.
  // Une mutation droppée (server error ou max attempts) ne compte pas
  // comme "synced" — elle a juste été retirée parce qu'elle ne servait
  // plus à rien de la garder.
  let drainedSuccess = 0;
  for (const m of pending) {
    if (m.seq === undefined) continue; // garde-fou ; ne devrait pas arriver

    // Phase 3.3.5 : route entre flow JSON simple et flow binaire R2.
    const outcome = m.media ? await replayMediaMutation(m) : await replayJsonMutation(m);

    if (outcome === 'success') {
      drainedSuccess += 1;
    } else if (outcome === 'keep') {
      // Réseau cassé pendant ce drain → on arrête tout, les
      // suivantes échoueront pareil.
      break;
    }
    // dropped : on continue avec la mutation suivante.
  }

  // Phase 3.3.4 : refresh du store + toast si quelque chose a été
  // drainé avec succès. Le `hydratePendingCount` re-query IDB pour
  // un compte exact (le bump optimiste de `notifyQueued` peut
  // diverger d'IDB après crash/reload).
  try {
    await hydratePendingCount();
    if (drainedSuccess > 0) {
      showToast({ message: t('mutations_synced'), kind: 'success' });
    }
  } catch (err) {
    console.warn('[mutations-replay] post-replay UI refresh failed:', err);
  }
}

/**
 * Câblage automatique au boot : écoute les `online` events du
 * navigateur et déclenche un replay à chaque reconnexion. À
 * appeler depuis `main.ts` une seule fois.
 *
 * Retourne une fonction de cleanup (utile pour les tests).
 */
export function installMutationsReplay(): () => void {
  // Premier drain au boot (best-effort, ne bloque pas le rendu).
  void replayPendingMutations();

  if (typeof window === 'undefined') return () => undefined;

  const handler = (): void => {
    void replayPendingMutations();
  };
  window.addEventListener('online', handler);
  return () => {
    window.removeEventListener('online', handler);
  };
}

/** @internal pour tests — reset le guard inFlight. */
export function _resetReplayInFlightForTests(): void {
  replayInFlight = null;
}
